/* Flappie Partner Finder — resolves the customer's PLZ, sorts partners by
   distance, filters by country + service chips, and shows certification only
   from the identity-bound Shopify registry embedded by the Liquid section.
   Scoped per section instance. Vanilla JS, no dependencies. */
(function () {
  "use strict";

  var SERVICE_LABELS = {
    montage: "Montage", glass_complete: "Glas-Komplettlösung",
    single_glazing: "Einfachglas", double_glazing: "Doppelglas",
    triple_glazing: "Dreifachglas", wooden_door: "Holztür",
    rental_ok: "Auch zur Miete", special_case: "Spezialfall"
  };
  var REACH_LABELS = { local: "lokal", regional: "reist weiter", national: "landesweit" };
  var FLAGS = { CH: "🇨🇭", DE: "🇩🇪", AT: "🇦🇹", FR: "🇫🇷", IT: "🇮🇹", NL: "🇳🇱", GB: "🇬🇧", BE: "🇧🇪", ES: "🇪🇸", PT: "🇵🇹", PL: "🇵🇱", SE: "🇸🇪", DK: "🇩🇰", FI: "🇫🇮", NO: "🇳🇴", IE: "🇮🇪", CZ: "🇨🇿", HU: "🇭🇺", GR: "🇬🇷", RO: "🇷🇴", SK: "🇸🇰", SI: "🇸🇮", HR: "🇭🇷", LU: "🇱🇺", BG: "🇧🇬", EE: "🇪🇪", LV: "🇱🇻", LT: "🇱🇹", CY: "🇨🇾", MT: "🇲🇹" };
  var COUNTRY_NAMES = { CH: "Schweiz", DE: "Deutschland", AT: "Österreich", FR: "Frankreich", IT: "Italien", NL: "Niederlande", GB: "Vereinigtes Königreich", BE: "Belgien", ES: "Spanien", PT: "Portugal", PL: "Polen", SE: "Schweden", DK: "Dänemark", FI: "Finnland", NO: "Norwegen", IE: "Irland", CZ: "Tschechien", HU: "Ungarn", GR: "Griechenland", RO: "Rumänien", SK: "Slowakei", SI: "Slowenien", HR: "Kroatien", LU: "Luxemburg", BG: "Bulgarien", EE: "Estland", LV: "Lettland", LT: "Litauen", CY: "Zypern", MT: "Malta" };
  var PARTNER_ID_RE = /^fp_[a-z2-7]{26}$/;
  var SHA256_RE = /^[0-9a-f]{64}$/;
  var CERTIFICATION_KEYS = [
    "activated_at", "badge", "contract_version", "directory_row_digest", "event_digest",
    "facts_digest", "public_partner_id", "state", "target_payload_digest"
  ];
  var DIRECTORY_ROW_KEYS = [
    "city", "countries_served", "country", "email", "identity_source", "inquiry_url",
    "lat", "logo", "lon", "name", "partner_id", "phone", "priority", "reach",
    "recommended", "row_digest", "services", "website"
  ];

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function haversineKm(aLat, aLon, bLat, bLon) {
    var R = 6371, toRad = Math.PI / 180;
    var dLat = (bLat - aLat) * toRad, dLon = (bLon - aLon) * toRad;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(aLat * toRad) * Math.cos(bLat * toRad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function sha256Hex(value) {
    if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
      return Promise.reject(new Error("SHA-256 unavailable"));
    }
    return window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))
      .then(function (buffer) {
        return Array.prototype.map.call(new Uint8Array(buffer), function (byte) {
          return byte.toString(16).padStart(2, "0");
        }).join("");
      });
  }

  function exactObjectKeys(value, expected) {
    return value && typeof value === "object" && !Array.isArray(value) &&
      Object.keys(value).sort().join("\u0000") === expected.slice().sort().join("\u0000");
  }

  function verificationRequest() {
    try {
      var params = new URL(window.location.href).searchParams;
      var ids = params.getAll("flappie_verify_partner");
      var countries = params.getAll("flappie_verify_country");
      if (!ids.length && !countries.length) return null;
      if (ids.length !== 1 || countries.length !== 1 || !PARTNER_ID_RE.test(ids[0]) ||
          !/^[A-Z]{2}$/.test(countries[0])) return { invalid: true };
      return { invalid: false, partnerId: ids[0], country: countries[0] };
    } catch (error) {
      return { invalid: true };
    }
  }

  function certificationCore(entry) {
    return {
      activated_at: entry.activated_at,
      badge: entry.badge,
      contract_version: entry.contract_version,
      directory_row_digest: entry.directory_row_digest,
      event_digest: entry.event_digest,
      facts_digest: entry.facts_digest,
      public_partner_id: entry.public_partner_id,
      state: entry.state
    };
  }

  function validateCertification(publicId, entry) {
    if (!PARTNER_ID_RE.test(publicId) || !exactObjectKeys(entry, CERTIFICATION_KEYS)) {
      return Promise.resolve(false);
    }
    if (entry.public_partner_id !== publicId || entry.badge !== "flappie-certified" ||
        entry.state !== "certified" || typeof entry.contract_version !== "string" ||
        !entry.contract_version || !SHA256_RE.test(entry.event_digest || "") ||
        !SHA256_RE.test(entry.directory_row_digest || "") ||
        !SHA256_RE.test(entry.facts_digest || "") ||
        !SHA256_RE.test(entry.target_payload_digest || "") ||
        typeof entry.activated_at !== "string" || !isFinite(Date.parse(entry.activated_at))) {
      return Promise.resolve(false);
    }
    return sha256Hex(JSON.stringify(certificationCore(entry))).then(function (digest) {
      return digest === entry.target_payload_digest;
    }).catch(function () { return false; });
  }

  function loadCertifications(section) {
    var node = section.querySelector("script[data-flappie-certification-registry]");
    var registry;
    try {
      registry = node ? JSON.parse(node.textContent || "") : null;
    } catch (error) {
      registry = null;
    }
    if (!exactObjectKeys(registry, ["schema_version", "certifications"]) ||
        registry.schema_version !== 1 || !registry.certifications ||
        typeof registry.certifications !== "object" || Array.isArray(registry.certifications)) {
      return Promise.resolve({});
    }
    var entries = registry.certifications;
    var publicIds = Object.keys(entries);
    return Promise.all(publicIds.map(function (publicId) {
      return validateCertification(publicId, entries[publicId]);
    })).then(function (validity) {
      var certified = {};
      publicIds.forEach(function (publicId, index) {
        if (validity[index]) certified[publicId] = entries[publicId];
      });
      return certified;
    });
  }

  function validatedPartners(payload) {
    if (!exactObjectKeys(payload, ["schema_version", "partners"]) ||
        payload.schema_version !== 2 || !Array.isArray(payload.partners)) return [];
    var seen = {};
    for (var i = 0; i < payload.partners.length; i += 1) {
      var row = payload.partners[i];
      if (!row || typeof row !== "object" || Array.isArray(row) ||
          !PARTNER_ID_RE.test(row.partner_id || "") || seen[row.partner_id] ||
          !exactObjectKeys(row, DIRECTORY_ROW_KEYS) ||
          !SHA256_RE.test(row.row_digest || "")) return [];
      seen[row.partner_id] = true;
    }
    return payload.partners;
  }

  function bindDirectoryRowDigests(rows) {
    rows.forEach(function (row) { row._directoryRowDigest = row.row_digest; });
    return Promise.resolve(rows);
  }

  function init(wrap) {
    var section = wrap.closest(".fl-finder") || wrap;
    Array.prototype.slice.call(
      section.querySelectorAll("[data-flappie-certification-binding]")
    ).forEach(function (marker) {
      if (marker.parentNode) marker.parentNode.removeChild(marker);
    });
    var verification = verificationRequest();
    var partnersUrl = wrap.getAttribute("data-partners-url");
    var limit = parseInt(wrap.getAttribute("data-limit"), 10) || 9;
    var verifiedBonus = parseFloat(wrap.getAttribute("data-verified-bonus")) || 0;

    var countrySel = wrap.querySelector(".fl-country");
    var plzInput = wrap.querySelector(".fl-plz");
    var goBtn = wrap.querySelector(".fl-go");
    var chips = Array.prototype.slice.call(wrap.querySelectorAll(".fl-chip"));
    var summary = wrap.querySelector(".fl-summary");
    var results = wrap.querySelector(".fl-results");
    var empty = wrap.querySelector(".fl-empty");

    var partners = [];
    var certified = {};
    var state = { service: "all", origin: null, plz: "" };
    var tableCache = {};
    var assetBase = partnersUrl.replace(/partners\.json(\?.*)?$/, "");

    function loadTable(cc) {
      if (tableCache[cc]) return Promise.resolve(tableCache[cc]);
      return fetch(assetBase + "plz-" + cc.toLowerCase() + ".json")
        .then(function (r) { return r.ok ? r.json() : {}; })
        .then(function (t) { tableCache[cc] = t; return t; })
        .catch(function () { return {}; });
    }

    function geocode(cc, plz) {
      var k = String(plz).trim().toUpperCase().replace(/\s+/g, "");
      return loadTable(cc).then(function (t) {
        var v = t[k];
        if (!v && cc === "NL") v = t[k.slice(0, 4)];
        if (!v && cc === "GB") v = t[k.slice(0, -3)] || t[k.split(" ")[0]];
        return v ? { lat: v[0], lon: v[1] } : null;
      });
    }

    function populateCountries() {
      var set = {};
      partners.forEach(function (p) {
        (p.countries_served || []).forEach(function (c) { if (c) set[c] = 1; });
      });
      var codes = Object.keys(set).sort(function (a, b) {
        return (COUNTRY_NAMES[a] || a).localeCompare(COUNTRY_NAMES[b] || b);
      });
      countrySel.innerHTML = codes.map(function (c) {
        return '<option value="' + c + '">' + (FLAGS[c] || "") + " " + esc(COUNTRY_NAMES[c] || c) + "</option>";
      }).join("");
      if (verification && !verification.invalid && set[verification.country]) {
        countrySel.value = verification.country;
      } else if (set.CH) countrySel.value = "CH";
    }

    Promise.all([
      fetch(partnersUrl).then(function (r) {
        if (!r.ok) throw new Error("Partner directory unavailable");
        return r.json();
      }).then(validatedPartners).then(bindDirectoryRowDigests),
      loadCertifications(section)
    ]).then(function (values) {
      partners = values[0];
      certified = values[1];
      if (verification) {
        partners = verification.invalid ? [] : partners.filter(function (partner) {
          return partner.partner_id === verification.partnerId &&
            Array.isArray(partner.countries_served) &&
            partner.countries_served.indexOf(verification.country) !== -1;
        });
        if (partners.length !== 1) partners = [];
      }
      populateCountries();
      render();
    }).catch(function () { partners = []; certified = {}; });

    function isCertified(p) {
      return !!certified[p.partner_id] &&
        certified[p.partner_id].directory_row_digest === p.row_digest;
    }

    function cardHtml(p) {
      var flag = FLAGS[p.country] || "";
      var dist = (typeof p._dist === "number")
        ? '<span class="fl-dist">' + (p._dist < 1 ? "<1" : Math.round(p._dist)) + " km entfernt</span>"
        : "";
      var reach = p.reach && REACH_LABELS[p.reach] ? esc(REACH_LABELS[p.reach]) : "";
      var badges = "";
      if (p.recommended) badges += '<span class="fl-badge is-reco">Empfohlen</span>';
      var directoryRowDigest = SHA256_RE.test(p._directoryRowDigest || "")
        ? p._directoryRowDigest : "";
      var certification = directoryRowDigest && isCertified(p) ? certified[p.partner_id] : null;
      if (certification) {
        badges += '<span class="fl-badge" data-flappie-partner-id="' + esc(p.partner_id) +
          '" data-flappie-directory-row-digest="' + esc(directoryRowDigest) +
          '" data-flappie-badge="flappie-certified" data-flappie-badge-visible="true"' +
          ' data-flappie-event-digest="' + esc(certification.event_digest) + '"' +
          ' data-flappie-facts-digest="' + esc(certification.facts_digest) + '"' +
          ' data-flappie-target-payload-digest="' +
          esc(certification.target_payload_digest) + '">Geprüfter Partner</span>';
      }

      var svcs = (p.services || []).map(function (k) {
        var hot = state.service === "all" || k === state.service;
        return '<span class="' + (hot ? "fl-svc is-primary" : "fl-svc") + '">' +
               esc(SERVICE_LABELS[k] || k) + "</span>";
      }).join("");

      var logo = p.logo
        ? '<img src="' + esc(p.logo) + '" alt="' + esc(p.name) + ' Logo" loading="lazy" ' +
          'onerror="this.style.display=\'none\'">'
        : "🔧";

      var href = p.inquiry_url ? esc(p.inquiry_url)
               : (p.email ? "mailto:" + esc(p.email) : "#");
      var companyName = p.website
        ? '<a class="fl-card-name" data-flappie-company-name ' +
          'data-flappie-company-website href="' + esc(p.website) +
          '" target="_blank" rel="noopener">' + esc(p.name) + "</a>"
        : '<div class="fl-card-name" data-flappie-company-name>' + esc(p.name) + "</div>";
      var meta = flag + " " + esc(p.city || "");
      if (reach) meta += " · " + reach;

      return '' +
        '<article class="fl-card' + (p.recommended ? " is-reco" : "") +
          '" data-flappie-partner-row="' + esc(p.partner_id) +
          '" data-flappie-directory-row-digest="' + esc(directoryRowDigest) + '">' +
          '<div class="fl-card-top">' +
            '<div class="fl-logo">' + logo + "</div>" +
            '<div class="fl-card-id">' +
              companyName +
              '<div class="fl-card-meta">' + meta + "</div>" +
            "</div>" +
            (dist || "") +
          "</div>" +
          (badges ? '<div class="fl-badges">' + badges + "</div>" : "") +
          '<div class="fl-svcs">' + svcs + "</div>" +
          '<a class="fl-cta" data-flappie-public-cta href="' + href +
          '" target="_blank" rel="noopener">Anfragen</a>' +
        "</article>";
    }

    function render() {
      var country = countrySel.value;
      var list = partners.filter(function (p) {
        var served = p.countries_served || [];
        if (served.indexOf(country) === -1) return false;
        if (state.service !== "all" && (p.services || []).indexOf(state.service) === -1) return false;
        return true;
      });

      list.forEach(function (p) {
        p._dist = state.origin ? haversineKm(state.origin.lat, state.origin.lon, p.lat, p.lon) : null;
        p._score = (p._dist == null ? 0 : p._dist) - (isCertified(p) ? verifiedBonus : 0) - (parseFloat(p.priority) || 0);
      });

      list.sort(function (a, b) {
        if (!!b.recommended !== !!a.recommended) return b.recommended ? 1 : -1;
        if (state.origin) return a._score - b._score;
        return String(a.name).localeCompare(String(b.name));
      });

      list = list.slice(0, limit);
      results.innerHTML = list.map(cardHtml).join("");
      empty.hidden = list.length > 0;

      if (state.origin && state.plz) {
        summary.hidden = false;
        summary.innerHTML = "<strong>" + list.length + " Partner</strong> kommen zu dir nach " +
          esc(state.plz) + ".";
      } else if (list.length) {
        summary.hidden = false;
        summary.innerHTML = "<strong>" + list.length + " Partner</strong> in " +
          esc(COUNTRY_NAMES[country] || country) +
          " – gib deine PLZ ein, um nach Nähe zu sortieren.";
      } else {
        summary.hidden = true;
      }
    }

    function search() {
      var plz = (plzInput.value || "").trim();
      state.plz = plz;
      if (!plz) { state.origin = null; render(); return; }
      goBtn.disabled = true;
      geocode(countrySel.value, plz).then(function (geo) {
        goBtn.disabled = false;
        if (!geo) {
          state.origin = null;
          summary.hidden = false;
          summary.innerHTML = "PLZ <strong>" + esc(plz) + "</strong> nicht gefunden – bitte prüfen.";
          results.innerHTML = ""; empty.hidden = true;
          return;
        }
        state.origin = { lat: geo.lat, lon: geo.lon };
        render();
      });
    }

    goBtn.addEventListener("click", search);
    plzInput.addEventListener("keydown", function (e) { if (e.key === "Enter") search(); });
    countrySel.addEventListener("change", function () { if (state.plz) search(); else render(); });
    chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        state.service = chip.getAttribute("data-svc");
        chips.forEach(function (c) { c.classList.toggle("is-active", c === chip); });
        render();
      });
    });
  }

  function boot() {
    document.querySelectorAll(".fl-finder .fl-wrap[data-partners-url]").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
